import { AlertColor } from '@mui/material';
import { Dispatch, useCallback, useState } from 'react';
import strings from '../../../../../../../common/Translation/Translate';
import Notification from '../../../../../../../components/Notification';
import { TimeslotMinimumRevenueCards } from './TimeslotMinimumRevenueCards';
import {
  TimeslotMinimumRevenueRow,
  validateTimeslotMinimumRevenueRow,
} from './timeslotFormUtils';

type AddEditTimeSlotMinimumRevenueProps = {
  editedData: TimeslotMinimumRevenueRow[];
  setData: Dispatch<TimeslotMinimumRevenueRow[]>;
};

export const AddEditTimeSlotMinimumRevenue: React.FC<AddEditTimeSlotMinimumRevenueProps> = ({
  editedData = [],
  setData,
}) => {
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({ display: false, severity: null, message: '' });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const setErrorForIndex = useCallback((index: number, row: TimeslotMinimumRevenueRow) => {
    const error = validateTimeslotMinimumRevenueRow(row);
    setErrors(prev => {
      const next = { ...prev };
      if (error) next[index] = error;
      else delete next[index];
      return next;
    });
    return error;
  }, []);

  const handleChange = (index: number, field: keyof TimeslotMinimumRevenueRow, value: string) => {
    const array = [...editedData];
    array[index] = { ...array[index], [field]: value };
    setData(array);
    setErrorForIndex(index, array[index]);
  };

  const handleBlur = (index: number) => {
    const row = editedData[index];
    if (!row) return;
    const error = setErrorForIndex(index, row);
    if (error) setMessage({ display: true, severity: 'error', message: error });
  };

  const handleDelete = (index: number) => {
    setData(editedData.filter((_, i) => i !== index));
    setErrors(prev => {
      const next: Record<number, string> = {};
      Object.entries(prev).forEach(([key, value]) => {
        const i = Number(key);
        if (i < index) next[i] = value;
        else if (i > index) next[i - 1] = value;
      });
      return next;
    });
  };

  const handleAdd = () => {
    setData([...editedData, { startTime: '', endTime: '', minimumRevenue: '' }]);
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <TimeslotMinimumRevenueCards
        rows={editedData}
        errors={errors}
        onChange={handleChange}
        onBlur={handleBlur}
        onDelete={handleDelete}
        onAdd={handleAdd}
      />
    </>
  );
};
